import React from "react";
import Layout, { Container } from "../components/layout";
import { removeCookie } from "../config/webStorage";
import { useUser } from "../config/userProvider";
import {
  Mail,
  Phone,
  User,
  ShieldCheck,
  ShieldX,
  Hash,
  Settings,
  Bell,
  Calendar,
  RefreshCw,
  LogOut,
} from "lucide-react";
import { motion } from "framer-motion";

const Account = () => {
  const { userData } = useUser();

  const logOutHandler = () => {
    removeCookie("swtoken");
    window.location.reload();
  };

  const Badge = ({ verified }) => (
    <span
      className={`ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
        verified ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {verified ? <ShieldCheck size={14} /> : <ShieldX size={14} />}
      {verified ? "Verified" : "Unverified"}
    </span>
  );

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="min-h-screen bg-gradient-to-br from-[#39160d] to-gray-100">
          {/* Header */}
          <div className="relative h-48 md:h-56 flex items-center justify-center">
            <div className="absolute -bottom-16 flex flex-col items-center">
              <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-white shadow-xl flex items-center justify-center text-7xl font-bold text-indigo-600">
                {userData.firstName?.[0]}
              </div>
              <h2 className="mt-3 text-xl md:text-2xl font-semibold text-white">
                {userData.firstName} {userData.lastName}
              </h2>
              <p className="text-sm text-white/80">{userData.userType}</p>
            </div>
          </div>

          {/* Content */}
          <div className="pt-28 px-4 md:px-10 pb-12 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {/* Email */}
              <div className="bg-white rounded-2xl shadow-md p-5 flex items-center justify-between hover:shadow-lg transition">
                <div className="flex items-center gap-3">
                  <Mail className="text-indigo-500" size={20} />
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-gray-600 text-sm">{userData.email}</p>
                  </div>
                </div>
                <Badge
                  verified={userData.verificationDetails.isEmailVerified}
                />
              </div>

              {/* Phone */}
              <div className="bg-white rounded-2xl shadow-md p-5 flex items-center justify-between hover:shadow-lg transition">
                <div className="flex items-center gap-3">
                  <Phone className="text-indigo-500" size={20} />
                  <div>
                    <p className="font-medium">Phone</p>
                    <p className="text-gray-600 text-sm">{userData.phone}</p>
                  </div>
                </div>
                <Badge
                  verified={userData.verificationDetails.isPhoneVerified}
                />
              </div>

              {/* User ID */}
              <div className="bg-white rounded-2xl shadow-md p-5 flex items-center gap-3 hover:shadow-lg transition">
                <Hash className="text-indigo-500" size={20} />
                <div>
                  <p className="font-medium">User ID</p>
                  <p className="text-gray-600 text-sm">{userData.id}</p>
                </div>
              </div>

              {/* Status */}
              <div className="bg-white rounded-2xl shadow-md p-5 flex items-center gap-3 hover:shadow-lg transition">
                <ShieldCheck className="text-indigo-500" size={20} />
                <div>
                  <p className="font-medium">Status</p>
                  <span
                    className={`mt-1 inline-block px-3 py-0.5 rounded-full text-xs font-medium ${
                      userData.status === "active"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {userData.status}
                  </span>
                </div>
              </div>

              {/* Language */}
              <div className="bg-white rounded-2xl shadow-md p-5 flex items-center gap-3 hover:shadow-lg transition">
                <Settings className="text-indigo-500" size={20} />
                <div>
                  <p className="font-medium">Language</p>
                  <p className="text-gray-600 text-sm">
                    {userData.preferences.language}
                  </p>
                </div>
              </div>

              {/* Theme */}
              <div className="bg-white rounded-2xl shadow-md p-5 flex items-center gap-3 hover:shadow-lg transition">
                <Settings className="text-indigo-500" size={20} />
                <div>
                  <p className="font-medium">Theme</p>
                  <p className="text-gray-600 text-sm">
                    {userData.preferences.theme}
                  </p>
                </div>
              </div>

              {/* Notifications */}
              <div className="bg-white rounded-2xl shadow-md p-5 md:col-span-2 hover:shadow-lg transition">
                <div className="flex items-center gap-3 mb-2">
                  <Bell className="text-indigo-500" size={20} />
                  <p className="font-medium">Notifications</p>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-gray-600 ml-1">
                  {Object.entries(userData.preferences.notifications).map(
                    ([type, value]) => (
                      <li key={type}>
                        {type.toUpperCase()}:{" "}
                        {value ? "Enabled ✅" : "Disabled ❌"}
                      </li>
                    )
                  )}
                </ul>
              </div>

              {/* Dates */}
              <div className="bg-white rounded-2xl shadow-md p-5 flex items-center gap-3 hover:shadow-lg transition">
                <Calendar className="text-indigo-500" size={20} />
                <div>
                  <p className="font-medium">Joined</p>
                  <p className="text-gray-600 text-sm">
                    {new Date(userData.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-md p-5 flex items-center gap-3 hover:shadow-lg transition">
                <RefreshCw className="text-indigo-500" size={20} />
                <div>
                  <p className="font-medium">Last Updated</p>
                  <p className="text-gray-600 text-sm">
                    {new Date(userData.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Logout Button */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12 flex justify-center"
            >
              <button
                onClick={logOutHandler}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl shadow-md transition duration-200 text-sm md:text-base"
              >
                <LogOut size={18} /> Logout
              </button>
            </motion.div>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default Account;
